import { useCallback, useMemo, useEffect, SyntheticEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';

import { DateTools, treezorLocaleConverters, TFileWithType } from '@treezor/design-system';

import { useI18nLocale } from '@/hooks/useI18nLocale';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-helpers';
import { useDashboardConfig } from '@/hooks/useDashboardConfig';

import { DashboardHelpers } from '@/utils/DashboardHelpers';

import { createUser } from '@/features/user/store/user.actions';
import { EParentType } from '@/features/user/store/user.model';
import {
  useCreateUserContext,
  IPostCreateUser,
  ITaxResidenceCreate,
  ICreateUserInfo,
} from '@/features/user/contexts/CreateUserContext';

import { useMatomo } from '@/features/matomo/useMatomo';
import { useUserInfo } from '@/features/user/hooks/useUserInfo';

export function useCreateUser(defaultValues?: IPostCreateUser) {
  const { t } = useTranslation();

  const config = useDashboardConfig();
  const distributionCountry = config?.settings?.users?.distributionCountry;
  const hasDistributionCountry = distributionCountry?.length > 0;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { triggerEvent } = useMatomo();

  const isLoading = useAppSelector((state) => state.profile.create?.isLoading);

  const { user, parentUser, updateUser, updateParentUser, setIsDirty } = useCreateUserContext();
  const { isPhysicalUser: isParentPhysicalUser, isSelfEmployed: isParentSelfEmployed } = useUserInfo(parentUser);
  const isDistributionCountryDisabled = hasDistributionCountry && !!parentUser && !!parentUser?.distributionCountry;

  const initialValues: IPostCreateUser = Object.entries(defaultValues || {}).reduce((acc, [key, initialValue]) => {
    acc[key] = user[key] || initialValue;
    return acc;
  }, {});

  // all the pre-set parentType declare here:
  const parentTypeProps = useMemo(() => {
    if (Object.keys(defaultValues || {}).includes('parentType')) {
      if (isParentSelfEmployed) {
        return { parentType: EParentType.employee };
      }
      if (isParentPhysicalUser && !isParentSelfEmployed) {
        return { parentType: EParentType.leader };
      }
    }

    return {};
  }, [isParentPhysicalUser, isParentSelfEmployed, defaultValues]);

  const distributionProps = useMemo(() => {
    if (Object.keys(defaultValues || {}).includes('distributionCountry')) {
      if (isDistributionCountryDisabled) {
        return { distributionCountry: parentUser.distributionCountry };
      }
    }

    return {};
  }, [isDistributionCountryDisabled, parentUser?.distributionCountry, defaultValues]);

  const methods = useForm<IPostCreateUser>({
    defaultValues: initialValues,
  });

  const {
    handleSubmit,
    getValues,
    watch,
    setValue,
    register,
    formState: { touchedFields },
  } = methods;

  const watches = Object.keys(defaultValues || {}).reduce((acc, key: keyof IPostCreateUser) => {
    acc[key] = watch(key);
    return acc;
  }, {});

  const isFormDirty = useMemo(() => {
    if (!user) {
      return false;
    }

    const { userTypeId, ...userData } = { ...user, ...watches };
    return Object.values(userData).some((value) => !!value);
  }, [user, watches]);

  const locale = useI18nLocale();
  const language = treezorLocaleConverters[locale];

  const handleValueChange = useCallback(
    (
      value: string | number | boolean | ITaxResidenceCreate[] | TFileWithType[] | ICreateUserInfo[],
      field: keyof IPostCreateUser,
    ) => setValue(field, value),
    [setValue],
  );

  const handleInputChange = useCallback(
    (event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>, field: keyof IPostCreateUser) => {
      const value = event.currentTarget.value;
      handleValueChange(value, field);
    },
    [handleValueChange],
  );

  const handleDateChange = useCallback(
    (date: Date, field: keyof IPostCreateUser) =>
      setValue(field, date ? DateTools.dateToLocalDateString(date, language) : undefined),
    [language, setValue],
  );

  useEffect(() => {
    setIsDirty(isFormDirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormDirty]);

  const handleSubmitForm = useCallback(() => {
    const { isSameAsPrincipalAddress, ...values } = getValues() || {};
    const newValues =
      isSameAsPrincipalAddress === 'yes' && parentUser
        ? {
            ...values,
            address1: parentUser.address1 || '',
            address2: parentUser.address2 || '',
            address3: parentUser.address3 || '',
            postcode: parentUser.postcode || '',
            country: parentUser.country || undefined,
            city: parentUser.city || '',
          }
        : values;

    updateUser({
      ...user,
      ...newValues,
      ...parentTypeProps,
      ...distributionProps,
    });
  }, [getValues, parentUser, updateUser, user, parentTypeProps, distributionProps]);

  const navigateDashboard = useCallback(
    (userId: number, shouldOpenNewTab?: boolean) => {
      if (shouldOpenNewTab) {
        window.open(DashboardHelpers.generateProfilePath(userId), '_blank');
      } else {
        navigate(DashboardHelpers.generateProfilePath(userId));
      }
    },
    [navigate],
  );

  const onCreateUser = useCallback(
    (user: IPostCreateUser, passphrase?: string, postActions?: (userId: number) => void | Promise<void>) => {
      const { roles, isSameAsPrincipalAddress, isAttachedToParent, ...newUser } = user;

      if (parentUser) {
        triggerEvent('user-children', { 'user-children-action': 'submit' });
      }
      dispatch(
        createUser(newUser, passphrase, (userId) => (postActions ? postActions(userId) : navigateDashboard(userId))),
      );
    },
    [dispatch, navigateDashboard, parentUser, triggerEvent],
  );

  const inputMaxLength = useCallback(
    (inputValue: string, maxLengthValue: number) => ({
      maxLengthValue,
      error:
        inputValue?.length > maxLengthValue ? t('pages.dashboard.createUser.form.rules.addressMaxLength') : undefined,
    }),
    [t],
  );

  const inputMaxNumber = useCallback(
    (inputNumber: number, maxNumber: number) => ({
      maxNumber,
      error:
        inputNumber > maxNumber ? t('pages.dashboard.createUser.form.rules.maxNumber', { max: maxNumber }) : undefined,
    }),
    [t],
  );

  const hasValidField = useCallback(
    (inputFields: string[]) => (inputFields || []).some((field: keyof IPostCreateUser) => !!watch(field)),
    [watch],
  );

  const hasInvalidInputLength = useCallback(
    (inputFields: string[]) =>
      inputFields.some((field: keyof IPostCreateUser) => !!inputMaxLength(watches[field], 35)?.error),
    [watches, inputMaxLength],
  );

  const shouldBeUSResident = useCallback(
    (declarativeData: (keyof IPostCreateUser)[]) =>
      declarativeData.some((key: keyof IPostCreateUser) => user?.[key] === 'US' || watches[key] === 'US'),
    [watches, user],
  );

  const handlePrefillData = useCallback(
    (data: IPostCreateUser) => {
      Object.entries(data || {}).map(([key, value]) => {
        if (value !== undefined) {
          handleValueChange(value, key as keyof IPostCreateUser);
          key === 'phone' && updateUser({ ...user, phone: value });
        }
      });
    },
    [handleValueChange, updateUser, user],
  );

  return {
    methods,
    config,
    handleSubmit: handleSubmit(handleSubmitForm),
    getValues,
    watch,
    setValue,
    register,
    touchedFields,
    inputMaxLength,
    watches,
    user,
    updateUser,
    language,
    handleInputChange,
    handleValueChange,
    handleDateChange,
    onCreateUser,
    isLoading,
    distributionCountry,
    hasDistributionCountry,
    isDistributionCountryDisabled,
    hasValidField,
    hasInvalidInputLength,
    navigateDashboard,
    shouldBeUSResident,
    inputMaxNumber,
    handlePrefillData,
    parentUser,
    updateParentUser,
    isParentSelfEmployed,
  };
}
